import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { Button, Form, Table, Spinner } from 'react-bootstrap';
import axios from 'axios';
import Layout from '../components/Layout';
import appConstants from '../constants/appConstants';
import { useAppContext } from '../context/AppContext';

const getAuthToken = () => localStorage.getItem('authToken');

// ✅ Define allowed fields here
const allowedFields = [
  'Ad. No',
  'Name',
  'Gender',
  'DOB',
  'Parents',
  'Mobile',
  'Class',
  'Division',
  'Address',
  'Medium',
  'School Name',
  'School Reg. No',
];
const fieldKeyMap = {
  'Ad. No': 'admission_no',
  Name: 'name',
  Gender: 'gender',
  DOB: 'dob',
  Parents: 'parents',
  Mobile: 'mobile',
  Class: 'class',
  Division: 'division',
  Address: 'address',
  Medium: 'medium',
  'School Name': 'school_name',
  'School Reg. No': 'school_reg_no',
};

const formatDateDDMMYYYY = (date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const formatDateDDMMYYYYUTC = (date) => {
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${day}-${month}-${year}`;
};

const formatDatePartsDDMMYYYY = (day, month, year) => {
  return `${String(day).padStart(2, '0')}-${String(month).padStart(
    2,
    '0',
  )}-${year}`;
};

const normalizeDobValue = (value) => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    // UI rule: always format Date objects using local timezone fields.
    return formatDateDDMMYYYY(value);
  }

  if (typeof value === 'number') {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed?.y && parsed?.m && parsed?.d) {
      return formatDatePartsDDMMYYYY(parsed.d, parsed.m, parsed.y);
    }
  }

  const textValue = String(value).trim().replace(/^'+/, '');

  const dmyMatch = textValue.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (dmyMatch) {
    const [, d, m, y] = dmyMatch;
    return `${d.padStart(2, '0')}-${m.padStart(2, '0')}-${y}`;
  }

  const ymdMatch = textValue.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (ymdMatch) {
    const [, y, m, d] = ymdMatch;
    return `${d.padStart(2, '0')}-${m.padStart(2, '0')}-${y}`;
  }

  return textValue;
};

const convertDobToBackendFormat = (value) => {
  if (!value) {
    return '';
  }

  const textValue = String(value).trim();

  const ymdMatch = textValue.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (ymdMatch) {
    const [, y, m, d] = ymdMatch;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  const dmyMatch = textValue.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (dmyMatch) {
    const [, d, m, y] = dmyMatch;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  return textValue;
};

const BulkUploadStudents = () => {
  const [parsedData, setParsedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { selectedAcademicYear } = useAppContext();

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (evt) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet, { raw: true });

      // Filter out only allowed fields
      const cleanedData = jsonData.map((row) => {
        const filtered = {};
        allowedFields.forEach((field) => {
          if (row[field] !== undefined && row[field] !== null) {
            filtered[field] =
              field === 'DOB' ? normalizeDobValue(row[field]) : row[field];
          }
        });
        return filtered;
      });

      setParsedData(cleanedData);
    };

    reader.readAsArrayBuffer(file);
  };

  useEffect(() => {}, [parsedData]);

  const handleSubmit = async () => {
    if (parsedData.length === 0) {
      alert('No valid data to submit.');
      return;
    }

    if (!selectedAcademicYear?.id) {
      alert('Please select an academic year.');
      return;
    }

    setLoading(true);
    try {
      const token = getAuthToken();

      // Convert keys to backend field names before sending
      /*const transformedData = parsedData.map((row) => {
      const transformedRow = {};
      Object.keys(row).forEach((userField) => {
        const backendField = fieldKeyMap[userField];
        if (backendField) {
          transformedRow[backendField] = row[userField];
        }
      });
      return transformedRow;
    });*/

      const transformedData = parsedData.map((row) => {
        const transformedRow = {};
        Object.keys(row).forEach((userField) => {
          const backendField = fieldKeyMap[userField];
          if (
            backendField &&
            backendField !== 'class' &&
            backendField !== 'division'
          ) {
            transformedRow[backendField] =
              backendField === 'dob'
                ? convertDobToBackendFormat(row[userField])
                : row[userField];
          }
        });

        // Handle multiple class/division combinations
        const classList = (row['Class'] || '')
          .toString()
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        const divisionList = (row['Division'] || '')
          .toString()
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);

        transformedRow.class_divisions = classList.map((cls, i) => ({
          class: cls,
          division: divisionList[i] || divisionList[0] || '',
        }));

        return transformedRow;
      });

      const response = await axios.post(
        `${appConstants.API_ROOT_URL}bulk-upload-students-v2?academic_year_id=${selectedAcademicYear.id}`,
        { students: transformedData },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.data.status === 'success') {
        // Check if partial errors exist in results
        const results = response.data.results || [];
        const errors = results.filter((r) => r.status === 'error');
        const successCount = results.length - errors.length;

        if (errors.length > 0) {
          alert(
            `Upload completed with some errors.\n` +
              `${successCount} students processed successfully.\n` +
              `Errors:\n` +
              errors
                .map((e) => `AdNo: ${e.admission_no || 'N/A'} - ${e.message}`)
                .join('\n'),
          );
        } else {
          alert('Upload successful! All students processed.');
        }

        setParsedData([]); // clear only on success or partial success
      } else {
        alert(response.data.message || 'Upload failed.');
      }
    } catch (err) {
      console.error('Upload error:', err);

      // More detailed message if available
      const message =
        err.response?.data?.message ||
        err.message ||
        'Failed to upload data. Please try again.';

      alert(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <h4>Bulk Upload Students</h4>
      <Form.Group className="mb-3">
        <Form.Label>Select Excel File</Form.Label>
        <Form.Control
          type="file"
          accept=".xlsx, .xls"
          onChange={handleFileUpload}
        />
      </Form.Group>

      {parsedData.length > 0 && (
        <>
          <h5>Preview</h5>
          <div className="table-responsive mb-3">
            <Table bordered hover>
              <thead className="table-light">
                <tr>
                  {allowedFields.map((field) => (
                    <th key={field}>{field}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {parsedData.map((row, index) => (
                  <tr key={index}>
                    {allowedFields.map((field) => (
                      <td key={field}>{row[field] || ''}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <Button variant="success" onClick={handleSubmit} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : 'Submit All'}
          </Button>
        </>
      )}
    </Layout>
  );
};

export default BulkUploadStudents;
